import type {
  BriefingCycleDto,
  SupplierBriefingDto,
  SupplierConditionsDto,
} from '@/api/supplier-conditions-client';

// =============================================================================
// saisie-header-model.ts — View-model PUR du HEADER contextuel de saisie (DBU §3.5).
// =============================================================================
// Le header de saisie (DBU §3.5 « Structure de la page ») présente : nom de la campagne,
// clients rattachés, cycle actif (Cycle 1 ou 2) et un compte à rebours J-X avant clôture.
// Ces données NE sont PAS portées par la grille (GrilleViewDto n'a que campaignId) : elles
// proviennent du briefing FOURNISSEUR déjà exposé (GET /supplier/conditions, §3.3), corrélé
// par `campaignId`. Ce module est PUR (aucun effet de bord, aucune requête) : il sélectionne
// le briefing, le cycle actif et calcule le compte à rebours. Aucune donnée back nouvelle.
//
// Clients rattachés : ce view-model NE lit PAS `briefing.clients` — la projection CONDITIONNELLE des
// bénéficiaires (§3.3 l.2608, sous gate de diffusion admin) vit dans conditions-screen, pas dans le header
// de saisie. Le compte à rebours double la couleur d'un LIBELLÉ TEXTE (WCAG 2.1 AA).

/** Un jour en millisecondes (base du compte à rebours). */
const DAY_MS = 24 * 60 * 60 * 1000;

/** Sélectionne le briefing de la campagne `campaignId` dans la réponse conditions (ou `null`). */
export function findBriefingForCampaign(
  conditions: SupplierConditionsDto | undefined,
  campaignId: string | undefined,
): SupplierBriefingDto | null {
  if (conditions === undefined || campaignId === undefined) {
    return null;
  }
  return conditions.campaigns.find((c) => c.campaignId === campaignId) ?? null;
}

/**
 * Détermine le cycle ACTIF d'un briefing (DBU §3.5 « cycle actif »). Priorité : le cycle marqué
 * `EnCours` par l'API (autorité) ; à défaut, le cycle dont le numéro correspond au `routeCycle`
 * d'URL (fenêtre d'affichage F-10) ; à défaut, le plus petit numéro de cycle. `null` si aucun cycle.
 */
export function findActiveCycle(
  cycles: readonly BriefingCycleDto[],
  routeCycle: number | undefined,
): BriefingCycleDto | null {
  if (cycles.length === 0) {
    return null;
  }
  const enCours = cycles.find((c) => c.status === 'EnCours');
  if (enCours !== undefined) {
    return enCours;
  }
  if (routeCycle !== undefined) {
    const matched = cycles.find((c) => c.cycleNumber === routeCycle);
    if (matched !== undefined) {
      return matched;
    }
  }
  return [...cycles].sort((a, b) => a.cycleNumber - b.cycleNumber)[0] ?? null;
}

/** Compte à rebours avant clôture (DBU §3.5). Le sens est porté par `label` (WCAG : jamais couleur seule). */
export interface DeadlineCountdown {
  /** Jours pleins restants avant clôture (0 = dernier jour ; négatif jamais renvoyé, voir `isPast`). */
  readonly daysRemaining: number;
  /** Échéance déjà dépassée (grille en lecture seule côté API). */
  readonly isPast: boolean;
  /** Jour de clôture (J-0) : dernier jour utile pour soumettre. */
  readonly isLastDay: boolean;
  /** Libellé TEXTE accessible du compte à rebours (autorité a11y ; double la couleur/badge). */
  readonly label: string;
  /** Tonalité d'affichage (renfort visuel) : `past` | `urgent` (J-0/J-1/J-2) | `normal`. */
  readonly tone: 'past' | 'urgent' | 'normal';
}

/**
 * Calcule le compte à rebours J-X depuis une date de clôture ISO 8601 et l'instant courant.
 * `null` si aucune date de clôture (cycle non planifié). Cas couverts : échéance dépassée
 * (`isPast`), dernier jour (`isLastDay`, J-0), et J-X standard. Aucune règle métier : le
 * verrouillage réel reste l'autorité de l'API (ce compte à rebours est informatif).
 */
export function computeDeadlineCountdown(
  closeAtIso: string | null,
  now: Date,
): DeadlineCountdown | null {
  if (closeAtIso === null) {
    return null;
  }
  const closeMs = new Date(closeAtIso).getTime();
  if (Number.isNaN(closeMs)) {
    return null;
  }
  const remainingMs = closeMs - now.getTime();
  if (remainingMs < 0) {
    return {
      daysRemaining: 0,
      isPast: true,
      isLastDay: false,
      label: 'Délai de clôture dépassé',
      tone: 'past',
    };
  }
  const daysRemaining = Math.floor(remainingMs / DAY_MS);
  if (daysRemaining === 0) {
    return {
      daysRemaining: 0,
      isPast: false,
      isLastDay: true,
      label: 'Clôture aujourd’hui (J-0)',
      tone: 'urgent',
    };
  }
  return {
    daysRemaining,
    isPast: false,
    isLastDay: false,
    label: `Clôture dans ${String(daysRemaining)} jour${daysRemaining > 1 ? 's' : ''} (J-${String(daysRemaining)})`,
    tone: daysRemaining <= 2 ? 'urgent' : 'normal',
  };
}
